// Models
import { IExerciseData } from 'storage/exercise/models'
import { TMenuDropdownOption } from 'heeds-ds/src/models'
import { IWorkoutSetInput, TSetSpecialSet, TSpecialSetInput } from 'models'

// React
import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

// Libraries
import { ThemeContext } from 'styled-components'
import { useFormContext } from 'react-hook-form'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import useMediaQuery from 'hooks/useMediaQuery'

// Components
import * as Styled from './styled'
import {
  Aligner,
  DropdownMenu,
  Icon,
  IconButton,
  InputCheckbox,
  InputText,
} from 'heeds-ds'
import { ExerciseForm } from 'components'

type Props = {
  displayMode?: boolean
  name: string
  onClickImage?: (exercise: IExerciseData) => void
  onEdit?: (workoutSet: TSpecialSetInput) => void
  readonly?: boolean
  removeExerciseFromSpecialWorkoutSet?: (exerciseIndex: number) => void
  removeWorkoutSet?: () => void
  replicateWorkoutSetFields?: (set: IWorkoutSetInput) => void
  setSpecialSet?: TSetSpecialSet
  showBorder?: boolean
  visible?: boolean
  workoutSet: IWorkoutSetInput & { index?: number }
}

const SpecialSeriesFormTag: FC<Props> = ({
  displayMode,
  name,
  onClickImage,
  onEdit,
  readonly,
  removeExerciseFromSpecialWorkoutSet,
  removeWorkoutSet,
  replicateWorkoutSetFields,
  setSpecialSet,
  showBorder,
  visible = true,
  workoutSet,
}) => {
  const { setValue, getFieldState, watch, resetField } = useFormContext()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.tablet}px)`)

  const [show, setShow] = useState(false)

  const { isDirty: someFieldIsDirty } = getFieldState(name)
  const { isDirty: repeatFieldIsDirty } = getFieldState(`${name}.repeat`)

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(`${name}.repeat`, event.target.checked)
    if (event.target.checked) {
      resetField(name, { defaultValue: watch(name) })
      const set = watch(name) as IWorkoutSetInput
      replicateWorkoutSetFields?.(set)
    }
  }

  const selectSpecialSerie = () => {
    const specialSet = {
      ...workoutSet,
      exercises: workoutSet.exercises.reduce(
        (acc, setToExercise) => ({ ...acc, [setToExercise.id]: setToExercise }),
        {},
      ),
    }
    setSpecialSet?.(specialSet)
    onEdit?.(specialSet)
  }

  const options: TMenuDropdownOption[] = [
    { icon: 'edit', label: 'Editar', onClick: () => selectSpecialSerie() },
    {
      icon: 'delete',
      color: theme.colors.action.criticalDefault,
      label: 'Excluir série especial',
      onClick: () => {
        removeWorkoutSet && removeWorkoutSet()
      },
    },
  ]

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    !isDesktop && setShow((prev) => !prev)
  }

  useEffect(() => {
    if (someFieldIsDirty && !repeatFieldIsDirty) {
      setValue(`${name}.repeat`, false)
    }
  }, [someFieldIsDirty, name, repeatFieldIsDirty, setValue])

  useLayoutEffect(() => {
    isDesktop && setShow(true)
  }, [isDesktop])

  return (
    <Styled.Container
      data-testid="special-series-form-tag-container"
      show={show}
      showBorder={showBorder}
      visible={visible}
    >
      <Aligner flex="column">
        <Aligner align="center" padding="0 8px 0 0">
          <Styled.Header
            data-testid="special-series-form-tag-header"
            onClick={handleClick}
          >
            <Aligner flex="column">
              <Styled.Title>Serie Especial</Styled.Title>

              {!show && workoutSet.exercises && (
                <Styled.SubTitle>
                  {`${workoutSet.exercises.length} exercício${
                    Object.values(workoutSet.exercises).length === 1 ? '' : 's'
                  }`}
                </Styled.SubTitle>
              )}
            </Aligner>

            {isDesktop && !readonly && (
              <DropdownMenu items={options}>
                <Styled.IconWrapper>
                  <Icon
                    size={24}
                    color={theme.colors.text.default}
                    iconName="moreVertical"
                  />
                </Styled.IconWrapper>
              </DropdownMenu>
            )}
          </Styled.Header>

          {!isDesktop && !displayMode && (
            <IconButton
              data-testid="special-series-form-tag-remove-button"
              iconName="check"
              onClick={removeWorkoutSet}
              size="small"
              track={buttonClickTracking}
              trackName="open_remove_confirmation_modal"
              variation="success"
            />
          )}
        </Aligner>

        <Styled.ContentInputs
          data-testid="special-series-form-tag-inputs"
          visible={show}
        >
          <InputText
            name={`${name}.quantity`}
            label="Série"
            margin="0"
            mask="integer"
            scale="small"
            placeholder="0"
            readOnly={readonly}
          />

          <InputText
            name={`${name}.interval`}
            label="Intervalo"
            margin="0"
            mask="seconds"
            placeholder="0 seg"
            scale="small"
            readOnly={readonly}
          />

          {!readonly && (
            <Styled.Label>
              <InputCheckbox
                checked={Boolean(watch(`${name}.repeat`))}
                onChange={handleCheckboxChange}
              />
              Repetir para todos os exercícios
            </Styled.Label>
          )}
        </Styled.ContentInputs>

        <Styled.ContentExercises visible={show}>
          {workoutSet.exercises &&
            workoutSet.exercises.length > 0 &&
            workoutSet.exercises.map((setToExercise, index) => {
              return (
                <ExerciseForm
                  key={setToExercise.id}
                  editMode={false}
                  exercise={setToExercise.exercise}
                  name={`${name}.exercises.${index}`}
                  onClickImage={onClickImage}
                  onRemove={() =>
                    removeExerciseFromSpecialWorkoutSet &&
                    removeExerciseFromSpecialWorkoutSet(index)
                  }
                  readonly={readonly}
                />
              )
            })}

          <Aligner padding="0 8px">
            <InputText
              name={`${name}.comments`}
              label="Comentários sobre a série especial"
              margin="0"
              placeholder="Digite aqui o comentário para o seu aluno"
              scale="small"
              readOnly={readonly}
            />
          </Aligner>
        </Styled.ContentExercises>
      </Aligner>
    </Styled.Container>
  )
}

export default SpecialSeriesFormTag
